/**
 * Guest intent bundle — merged into Shopper Model on signup/login.
 */

const DEVICE_KEY = "simpary_guest_device_id";
const SESSION_IDS_KEY = "guest_session_ids";
const SNAPSHOTS_KEY = "guest_intent_snapshots";
const CHOICE_KEY = "guest_decision_choice";
const MAX_SNAPSHOTS = 10;
const MAX_SESSION_IDS = 25;

export function getOrCreateGuestDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function registerGuestSessionId(sessionId) {
  if (!sessionId) return;
  try {
    const list = JSON.parse(localStorage.getItem(SESSION_IDS_KEY) || "[]");
    const next = [sessionId, ...list.filter((s) => s !== sessionId)].slice(
      0,
      MAX_SESSION_IDS,
    );
    localStorage.setItem(SESSION_IDS_KEY, JSON.stringify(next));
  } catch {
    localStorage.setItem(SESSION_IDS_KEY, JSON.stringify([sessionId]));
  }
}

export function rememberGuestChoice(choice) {
  if (!choice?.sessionId || !choice?.productId) return;
  try {
    localStorage.setItem(
      CHOICE_KEY,
      JSON.stringify({
        sessionId: choice.sessionId,
        productId: choice.productId,
        tradeoff: choice.tradeoff || "",
        reason: choice.reason || "",
        pickedAt: choice.pickedAt || new Date().toISOString(),
        shareToken: choice.shareToken || undefined,
      }),
    );
  } catch {
    /* ignore quota */
  }
}

export function clearRememberedGuestChoice() {
  try {
    localStorage.removeItem(CHOICE_KEY);
  } catch {
    /* ignore */
  }
}

function readRememberedGuestChoice() {
  try {
    const raw = localStorage.getItem(CHOICE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.sessionId || !parsed?.productId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function appendGuestIntentSnapshot(snapshot = {}) {
  if (!snapshot || typeof snapshot !== "object") return;
  try {
    const list = JSON.parse(localStorage.getItem(SNAPSHOTS_KEY) || "[]");
    list.unshift({
      ...snapshot,
      guestDeviceId: getOrCreateGuestDeviceId(),
      capturedAt: new Date().toISOString(),
    });
    localStorage.setItem(
      SNAPSHOTS_KEY,
      JSON.stringify(list.slice(0, MAX_SNAPSHOTS)),
    );
  } catch {
    /* ignore quota errors */
  }
}

export function buildGuestMergePayload() {
  let sessionIds = [];
  let snapshots = [];
  try {
    sessionIds = JSON.parse(localStorage.getItem(SESSION_IDS_KEY) || "[]");
  } catch {
    sessionIds = [];
  }
  try {
    snapshots = JSON.parse(localStorage.getItem(SNAPSHOTS_KEY) || "[]");
  } catch {
    snapshots = [];
  }

  const currentSessionId = localStorage.getItem("currentSessionId");
  if (currentSessionId && !sessionIds.includes(currentSessionId)) {
    sessionIds = [currentSessionId, ...sessionIds].slice(0, MAX_SESSION_IDS);
  }

  const wizardAnswersRaw = localStorage.getItem("wizardAnswers");
  const matchProfileRaw = localStorage.getItem("matchProfile");
  if (wizardAnswersRaw || matchProfileRaw) {
    let wizardAnswers = {};
    let matchProfile = null;
    try {
      wizardAnswers = wizardAnswersRaw ? JSON.parse(wizardAnswersRaw) : {};
    } catch {
      wizardAnswers = {};
    }
    try {
      matchProfile = matchProfileRaw ? JSON.parse(matchProfileRaw) : null;
    } catch {
      matchProfile = null;
    }
    if (Object.keys(wizardAnswers).length || matchProfile) {
      snapshots = [
        {
          sessionId: currentSessionId || undefined,
          wizardAnswers,
          matchProfile,
          guestDeviceId: getOrCreateGuestDeviceId(),
        },
        ...snapshots,
      ].slice(0, MAX_SNAPSHOTS);
    }
  }

  const remembered = readRememberedGuestChoice();
  if (remembered?.sessionId) {
    const already = snapshots.some(
      (snapshot) => snapshot.sessionId === remembered.sessionId,
    );
    snapshots = already
      ? snapshots.map((snapshot) =>
          snapshot.sessionId === remembered.sessionId
            ? { ...snapshot, choice: remembered }
            : snapshot,
        )
      : [{ ...remembered, guestDeviceId: getOrCreateGuestDeviceId() }, ...snapshots];
    snapshots = snapshots.slice(0, MAX_SNAPSHOTS);
  }

  return {
    guestDeviceId: getOrCreateGuestDeviceId(),
    sessionIds,
    snapshots,
  };
}

export function clearGuestIntentAfterMerge() {
  localStorage.removeItem(SESSION_IDS_KEY);
  localStorage.removeItem(SNAPSHOTS_KEY);
  localStorage.removeItem(CHOICE_KEY);
}

export async function mergeGuestIntentAfterAuth(api) {
  const payload = buildGuestMergePayload();
  const hasWork =
    payload.sessionIds?.length > 0 ||
    payload.snapshots?.length > 0 ||
    payload.guestDeviceId;

  if (!hasWork) return null;

  try {
    const res = await api.post("/users/me/merge-guest-intent", payload);
    clearGuestIntentAfterMerge();
    return res.data;
  } catch (err) {
    console.warn("Guest intent merge failed:", err.response?.data || err.message);
    return null;
  }
}
